import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  getStravaAuthUrl,
  exchangeToken,
  refreshStravaToken,
  fetchAthleteProfile,
  fetchActivities,
  fetchActivityDetails
} from '../lib/strava';

const StravaContext = createContext();

export const useStrava = () => {
  const context = useContext(StravaContext);
  if (!context) {
    throw new Error('useStrava must be used within a StravaProvider');
  }
  return context;
};

export const StravaProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [tokenExpiry, setTokenExpiry] = useState(null);
  const [athlete, setAthlete] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [demoMode, setDemoMode] = useState(false);

  // Initialize from local storage
  useEffect(() => {
    const storedToken = localStorage.getItem('strava_access_token');
    const storedRefreshToken = localStorage.getItem('strava_refresh_token');
    const storedExpiry = localStorage.getItem('strava_token_expiry');
    const storedDemoMode = localStorage.getItem('strava_demo_mode') === 'true';

    if (storedDemoMode) {
      setDemoMode(true);
      setIsAuthenticated(true);
      generateMockData();
      return;
    }

    if (storedToken && storedRefreshToken && storedExpiry) {
      setAccessToken(storedToken);
      setRefreshToken(storedRefreshToken);
      setTokenExpiry(parseInt(storedExpiry));

      // Check if token is expired
      if (Date.now() > parseInt(storedExpiry) * 1000) {
        handleTokenRefresh(storedRefreshToken);
      } else {
        setIsAuthenticated(true);
        loadUserData(storedToken);
      }
    }
  }, []);

  // Handle token refresh when needed
  const handleTokenRefresh = async (refresh) => {
    try {
      const data = await refreshStravaToken(refresh);
      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setTokenExpiry(data.expires_at);
      setIsAuthenticated(true);

      // Update local storage
      localStorage.setItem('strava_access_token', data.access_token);
      localStorage.setItem('strava_refresh_token', data.refresh_token);
      localStorage.setItem('strava_token_expiry', data.expires_at);

      return data.access_token;
    } catch (error) {
      console.error('Failed to refresh token:', error);
      handleLogout();
      throw error;
    }
  };

  // Load user data (athlete profile and activities)
  const loadUserData = async (token) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch athlete profile
      const athleteData = await fetchAthleteProfile(token);
      setAthlete(athleteData);

      // Fetch activities
      const activitiesData = await fetchActivities(token);

      // Fetch detailed data for each activity
      const detailedActivities = await Promise.all(
        activitiesData.map(async (activity) => {
          try {
            return await fetchActivityDetails(token, activity.id);
          } catch (error) {
            console.error(`Error fetching details for activity ${activity.id}:`, error);
            return activity;
          }
        })
      );

      setActivities(detailedActivities);
    } catch (error) {
      console.error('Error loading user data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle OAuth callback
  const handleAuthCallback = async (code) => {
    try {
      setLoading(true);
      setError(null);
      console.log("Processing authentication callback with code:", code);

      // Exchange code for token
      const data = await exchangeToken(code);
      console.log("Token exchange successful:", data);

      setAccessToken(data.access_token);
      setRefreshToken(data.refresh_token);
      setTokenExpiry(data.expires_at);
      setIsAuthenticated(true);
      setDemoMode(false);

      // Store in localStorage
      localStorage.setItem('strava_access_token', data.access_token);
      localStorage.setItem('strava_refresh_token', data.refresh_token);
      localStorage.setItem('strava_token_expiry', data.expires_at);
      localStorage.setItem('strava_demo_mode', 'false');

      // Load user data
      await loadUserData(data.access_token);
      return true;
    } catch (error) {
      console.error('Auth callback error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Initiate Strava authentication
  const authenticateWithStrava = () => {
    const authUrl = getStravaAuthUrl();
    console.log("Redirecting to Strava authorization:", authUrl);
    window.location.href = authUrl;
  };

  // Handle logout
  const handleLogout = () => {
    setIsAuthenticated(false);
    setAccessToken(null);
    setRefreshToken(null);
    setTokenExpiry(null);
    setAthlete(null);
    setActivities([]);
    setDemoMode(false);

    localStorage.removeItem('strava_access_token');
    localStorage.removeItem('strava_refresh_token');
    localStorage.removeItem('strava_token_expiry');
    localStorage.removeItem('strava_demo_mode');
  };

  // Enable demo mode
  const enableDemoMode = () => {
    setDemoMode(true);
    setIsAuthenticated(true);
    localStorage.setItem('strava_demo_mode', 'true');
    generateMockData();
  };

  // Generate mock data for demo mode
  const generateMockData = () => {
    // Mock athlete data
    setAthlete({
      id: 12345,
      firstname: 'Demo',
      lastname: 'Runner',
      profile_medium: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=100&h=100&fit=crop&crop=face'
    });

    // Mock activities
    const mockActivities = [
      {
        id: 1,
        name: 'Morning Tempo Run',
        distance: 8047,
        moving_time: 2400,
        elapsed_time: 2520,
        total_elevation_gain: 45,
        type: 'Run',
        start_date: '2024-01-15T07:30:00Z',
        average_speed: 3.35,
        max_speed: 4.2,
        splits_metric: generateMockSplits(8.047, 2400),
        map: { summary_polyline: 'mock_polyline_data' }
      },
      {
        id: 2,
        name: 'Long Sunday Run',
        distance: 15200,
        moving_time: 5400,
        elapsed_time: 5600,
        total_elevation_gain: 120,
        type: 'Run',
        start_date: '2024-01-14T08:15:00Z',
        average_speed: 2.8,
        max_speed: 3.6,
        splits_metric: generateMockSplits(15.2, 5400),
        map: { summary_polyline: 'mock_polyline_data' }
      },
      {
        id: 3,
        name: 'Interval Training',
        distance: 5000,
        moving_time: 1500,
        elapsed_time: 1800,
        total_elevation_gain: 25,
        type: 'Run',
        start_date: '2024-01-12T17:30:00Z',
        average_speed: 3.5,
        max_speed: 5.2,
        splits_metric: generateMockSplits(5.0, 1500),
        map: { summary_polyline: 'mock_polyline_data' }
      },
      {
        id: 4,
        name: 'Recovery Run',
        distance: 3200,
        moving_time: 1200,
        elapsed_time: 1250,
        total_elevation_gain: 15,
        type: 'Run',
        start_date: '2024-01-11T06:45:00Z',
        average_speed: 2.7,
        max_speed: 3.1,
        splits_metric: generateMockSplits(3.2, 1200),
        map: { summary_polyline: 'mock_polyline_data' }
      },
      {
        id: 5,
        name: 'Afternoon Ride',
        distance: 25000,
        moving_time: 4500,
        elapsed_time: 4800,
        total_elevation_gain: 350,
        type: 'Ride',
        start_date: '2024-01-10T14:00:00Z',
        average_speed: 5.5,
        max_speed: 12.2,
        splits_metric: generateMockSplits(25.0, 4500),
        map: { summary_polyline: 'mock_polyline_data' }
      }
    ];

    setActivities(mockActivities);
  };

  // Generate mock splits for demo activities
  const generateMockSplits = (distanceKm, totalTimeSeconds) => {
    const splits = [];
    const avgPacePerKm = totalTimeSeconds / distanceKm;

    for (let i = 0; i < Math.floor(distanceKm); i++) {
      splits.push({
        distance: 1000,
        elapsed_time: avgPacePerKm + (Math.random() - 0.5) * 60,
        elevation_difference: Math.random() * 20 - 10,
        moving_time: avgPacePerKm * 0.98,
        pace_zone: Math.floor(Math.random() * 5) + 1,
        split: i + 1
      });
    }

    return splits;
  };

  // Refresh activities
  const refreshActivities = async () => {
    if (demoMode) {
      generateMockData();
      return;
    }

    if (!isAuthenticated || !accessToken) {
      setError('Not authenticated');
      return;
    }

    try {
      setLoading(true);

      // Check if token needs refreshing
      if (tokenExpiry && Date.now() > tokenExpiry * 1000) {
        const newToken = await handleTokenRefresh(refreshToken);
        await loadUserData(newToken);
      } else {
        await loadUserData(accessToken);
      }
    } catch (error) {
      console.error('Error refreshing activities:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Get a specific activity by ID
  const getActivityById = (id) => {
    return activities.find(activity => activity.id.toString() === id.toString()) || null;
  };

  // Context value
  const value = {
    isAuthenticated,
    accessToken,
    athlete,
    activities,
    loading,
    error,
    demoMode,
    authenticateWithStrava,
    handleAuthCallback,
    logout: handleLogout,
    refreshActivities,
    getActivityById,
    enableDemoMode
  };

  return (
    <StravaContext.Provider value={value}>
      {children}
    </StravaContext.Provider>
  );
};